import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Phone } from "lucide-react";
import { applySeo, heroLines } from "../lib/seo";
import { CONTACT } from "../data/site";
import { PRO_PAGES, PRO_CASE_STUDIES, PRO_SPOTLIGHT } from "../data/prosPages";
import { MaskedLines, Reveal, SectionHeading } from "../components/site/Reveal";
import { BrandMarquee } from "../components/site/BrandMarquee";
import { CTABand } from "../components/site/CTABand";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const AF = "/architects/files";

export default function ProfessionalPage() {
  const { pathname } = useLocation();
  const path = pathname.endsWith("/") ? pathname : `${pathname}/`;
  const cfg = PRO_PAGES[path] || PRO_PAGES["/architects/"];
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 130]);

  useEffect(() => {
    applySeo({ paths: [path], fallbackTitle: cfg.seoFallback });
    window.scrollTo(0, 0);
  }, [path, cfg]);

  const lines = heroLines(cfg.heroLines, path);

  return (
    <main data-testid={cfg.testid}>
      {/* HERO */}
      <section className="relative min-h-[86vh] overflow-hidden flex items-center pt-32 pb-16">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={cfg.heroImage} alt={cfg.heroAlt} className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A15]/90 via-[#050A15]/60 to-[#050A15]/35" />
        </motion.div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
          <Reveal delay={0.1}>
            <p className="overline-x mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span className="mx-2">/</span>
              Professionals
            </p>
          </Reveal>
          <MaskedLines as="h1" lines={lines} delay={0.2}
            className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02] max-w-4xl" />
          <Reveal delay={0.55} className="mt-7">
            <h2 className="text-base md:text-lg text-white/75 max-w-xl font-normal">{cfg.heroSub}</h2>
          </Reveal>
          <Reveal delay={0.7} className="mt-9 flex flex-wrap gap-4">
            <Link to="/contact/" data-testid="pro-hero-cta" className="btn-solid">
              {cfg.heroCta || "Schedule a call"} <ArrowRight size={14} />
            </Link>
            <a href="#enquiry" data-testid="pro-hero-enquiry" className="btn-ghost">Quick enquiry</a>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[#F4F4F2]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading number="01" kicker={cfg.intro.kicker} title={cfg.intro.heading} light />
            <Reveal className="-mt-8 space-y-5">
              {cfg.intro.paragraphs.map((p) => (
                <p key={p.slice(0, 30)} className="leading-relaxed" style={{ color: "#3a4a58" }}>{p}</p>
              ))}
              <img src={`${AF}/small-10-FFB.png`} alt="FENSA Registered Company and GGFi Guarantee Protection" loading="lazy" className="h-16 w-auto object-contain mt-6" />
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            {cfg.intro.video ? (
              <div className="aspect-video w-full overflow-hidden shadow-xl bg-black" data-testid="pro-video">
                <iframe src={cfg.intro.video} title={cfg.intro.heading} allow="fullscreen" allowFullScreen className="w-full h-full border-0" />
              </div>
            ) : cfg.intro.sideImage ? (
              <div className="img-hover shadow-xl">
                <img src={cfg.intro.sideImage} alt={cfg.intro.heading} loading="lazy" className="w-full h-auto object-cover" />
              </div>
            ) : null}
            {cfg.intro.image && <img src={cfg.intro.image} alt={cfg.intro.heading} loading="lazy" className="w-full h-64 object-cover mt-6" />}
          </Reveal>
        </div>
      </section>

      {/* WHY */}
      <section className="border-t hairline bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeading number="02" kicker={cfg.why.kicker} title={cfg.why.heading} />
          <Reveal className="-mt-10 mb-14 max-w-2xl">
            <p className="text-white/60 leading-relaxed">{cfg.why.sub}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {cfg.why.items.map((w, i) => (
              <Reveal key={w.t} delay={(i % 3) * 0.1} className="bg-[#070E1D] p-9 lg:p-11">
                <span className="font-mono-x text-xs text-[#5B7F9E]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display font-medium text-xl text-white mt-8 mb-4">{w.t}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{w.d}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2} className="relative min-h-[280px] overflow-hidden">
              <img src={cfg.why.image} alt={cfg.why.heading} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#F4F4F2]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <SectionHeading number="03" kicker={cfg.process.kicker} title={cfg.process.heading} light />
          <Reveal className="-mt-10 mb-14 max-w-2xl">
            <p className="leading-relaxed" style={{ color: "#3a4a58" }}>{cfg.process.intro}</p>
          </Reveal>
          <div className="max-w-3xl" data-testid="pro-process">
            {cfg.process.stages.map((st, i) => (
              <Reveal key={st.t} delay={i * 0.06} className="relative pl-10 lg:pl-14 pb-12 last:pb-0 border-l border-[#31465A]/25 ml-2">
                <span className="absolute -left-[9px] top-1 w-[17px] h-[17px] rounded-full bg-[#31465A] border-4 border-[#F4F4F2]" />
                <p className="font-mono-x text-[11px] tracking-[0.25em] uppercase text-[#31465A]">{st.s}</p>
                <h3 className="font-display uppercase tracking-tight text-xl lg:text-2xl mt-2" style={{ color: "#24333F" }}>{st.t}</h3>
                <p className="text-sm leading-relaxed mt-3 max-w-2xl" style={{ color: "#3a4a58" }}>{st.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REGION NOTE */}
      {cfg.region && (
        <section className="bg-white border-t hairline">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <Reveal>
              <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl" style={{ color: "#24333F" }}>{cfg.region.heading}</h2>
              {cfg.region.paragraphs.map((p) => (
                <p key={p.slice(0, 30)} className="leading-relaxed mt-5" style={{ color: "#3a4a58" }}>{p}</p>
              ))}
            </Reveal>
          </div>
        </section>
      )}

      {/* CREDENTIALS */}
      <section className="bg-[#31465A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="overline-x">{cfg.creds.overline}</p>
            <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl text-white mt-4">{cfg.creds.heading}</h2>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/contact/" data-testid="pro-creds-cta" className="btn-dark">
                {cfg.creds.ctaLabel} <ArrowRight size={14} />
              </Link>
              <a href="#enquiry" className="btn-ghost">Quick enquiry</a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <ul className="space-y-4" data-testid="pro-credentials">
              {cfg.creds.items.map((c) => (
                <li key={c} className="flex items-start gap-4 text-white/85 text-sm leading-relaxed">
                  <Check size={16} className="text-[#EFE6D0] mt-0.5 shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="border-t hairline bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeading number="04" kicker="Recent case studies" title="Some of our most recent projects." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRO_CASE_STUDIES.map((cs, i) => (
              <Reveal key={cs.t} delay={(i % 3) * 0.08} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <Link to="/projects/" data-testid={`pro-case-${i}`} className="group block relative overflow-hidden aspect-square">
                  <img src={cs.img} alt={cs.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050A15]/90 via-[#050A15]/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/50">{cs.loc}</p>
                    <h3 className="font-display font-medium text-lg text-white mt-1.5 leading-snug">{cs.t}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
            <Reveal delay={0.16}>
              <Link to="/projects/" data-testid="pro-all-projects" className="group flex flex-col items-start justify-end aspect-square border border-white/15 p-6 hover:bg-white/5 transition-colors">
                <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/40">Portfolio</p>
                <h3 className="font-display font-medium text-lg text-white mt-1.5">View the full portfolio</h3>
                <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 group-hover:text-white transition-colors">
                  Explore <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PARTNER SPOTLIGHT */}
      <section className="bg-white border-y hairline">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center" data-testid="pro-spotlight">
          <Reveal>
            <p className="overline-x" style={{ color: "rgba(30,42,54,0.55)" }}>Partner spotlight</p>
            <blockquote className="font-display text-2xl sm:text-3xl leading-snug mt-8 max-w-3xl mx-auto" style={{ color: "#24333F" }}>
              "{PRO_SPOTLIGHT.quote}"
            </blockquote>
            <div className="flex items-center justify-center gap-5 mt-10">
              {PRO_SPOTLIGHT.img && <img src={PRO_SPOTLIGHT.img} alt={PRO_SPOTLIGHT.name} loading="lazy" className="w-16 h-16 rounded-full object-cover" />}
              <div className="text-left">
                <p className="font-semibold text-sm" style={{ color: "#24333F" }}>{PRO_SPOTLIGHT.name}</p>
                <p className="text-xs mt-1" style={{ color: "#3a4a58" }}>{PRO_SPOTLIGHT.role}</p>
              </div>
            </div>
            {PRO_SPOTLIGHT.logo && <img src={PRO_SPOTLIGHT.logo} alt={PRO_SPOTLIGHT.name} loading="lazy" className="h-10 w-auto object-contain mx-auto mt-8" />}
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F4F4F2]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <SectionHeading number="05" kicker="Trusted across Surrey, Hampshire and the South East" title={cfg.faqKicker} light />
          <Accordion type="single" collapsible className="w-full -mt-6" data-testid="pro-faq">
            {cfg.faq.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#31465A]/15">
                <AccordionTrigger data-testid={`pro-faq-trigger-${i}`} className="text-left font-display text-base lg:text-lg hover:no-underline py-6" style={{ color: "#24333F" }}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed pb-6" style={{ color: "#3a4a58" }}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <BrandMarquee />

      {/* CTA */}
      <section className="bg-[#070E1D] border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <Reveal>
            <p className="overline-x">{cfg.cta.overline}</p>
            <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl text-white mt-3 max-w-2xl">{cfg.cta.heading}</h2>
            {cfg.cta.sub && <p className="text-white/60 leading-relaxed mt-4 max-w-xl text-sm">{cfg.cta.sub}</p>}
          </Reveal>
          <Reveal delay={0.1} className="flex flex-wrap gap-4 shrink-0">
            <Link to="/contact/" data-testid="pro-cta" className="btn-solid">
              Book a technical consultation <ArrowRight size={14} />
            </Link>
            <a href={CONTACT.phoneHref} className="btn-ghost">
              <Phone size={14} /> {CONTACT.phone}
            </a>
          </Reveal>
        </div>
      </section>

      <CTABand product="Windows & Doors" />
    </main>
  );
}
